import React, { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/components/AuthContext";

export const SITE_POPUP_TRIGGER_EVENT = "joybase:site-popup-trigger";

type PopupTriggerDetail = {
  triggerType: "lottery_result";
  lotteryResultIds?: number[];
};

let pendingTrigger: PopupTriggerDetail | null = null;

export type ReservedPopup = {
  delivery_uid: string;
  banner_image_url: string;
  banner_alt: string;
  link_url: string;
};

export function requestSitePopup(detail: PopupTriggerDetail) {
  pendingTrigger = detail;
  window.dispatchEvent(new CustomEvent<PopupTriggerDetail>(SITE_POPUP_TRIGGER_EVENT, { detail }));
}

const SitePopupHost: React.FC = () => {
  const { currentUser } = useAuth();
  const [popup, setPopup] = useState<ReservedPopup | null>(null);
  const displayedUid = useRef<string | null>(null);
  const reserving = useRef(false);
  const pendingRegistrationFetched = useRef(false);

  const track = useCallback(async (deliveryUid: string, event: "displayed" | "clicked" | "dismissed") => {
    await apiClient.patch(`/customer/popup_deliveries/${deliveryUid}`, { event });
  }, []);

  const reserve = useCallback(async (detail: PopupTriggerDetail) => {
    if (reserving.current || popup) return;

    reserving.current = true;
    try {
      const response = await apiClient.post("/customer/popups/reserve", {
        trigger_type: detail.triggerType,
        lottery_result_ids: detail.lotteryResultIds,
      });
      setPopup(response.data.site_popup || null);
    } catch (error) {
      console.error("[SitePopup] Failed to reserve popup", error);
    } finally {
      reserving.current = false;
    }
  }, [popup]);

  useEffect(() => {
    const consumeTrigger = (detail: PopupTriggerDetail) => {
      pendingTrigger = null;
      reserve(detail);
    };
    const listener = (event: Event) => consumeTrigger((event as CustomEvent<PopupTriggerDetail>).detail);
    window.addEventListener(SITE_POPUP_TRIGGER_EVENT, listener);
    if (pendingTrigger) consumeTrigger(pendingTrigger);
    return () => window.removeEventListener(SITE_POPUP_TRIGGER_EVENT, listener);
  }, [reserve]);

  useEffect(() => {
    if (!currentUser) return;
    if (pendingRegistrationFetched.current) return;

    pendingRegistrationFetched.current = true;
    apiClient.get("/customer/popup_deliveries/pending")
      .then((response) => setPopup(response.data.site_popup || null))
      .catch((error) => console.error("[SitePopup] Failed to fetch pending registration popup", error));
  }, [currentUser]);

  useEffect(() => {
    if (!popup || displayedUid.current === popup.delivery_uid) return;

    displayedUid.current = popup.delivery_uid;
    track(popup.delivery_uid, "displayed").catch((error) => {
      console.error("[SitePopup] Failed to track display", error);
    });
  }, [popup, track]);

  const dismiss = () => {
    if (!popup) return;

    const deliveryUid = popup.delivery_uid;
    setPopup(null);
    track(deliveryUid, "dismissed").catch((error) => {
      console.error("[SitePopup] Failed to track dismissal", error);
    });
  };

  const openLink = async () => {
    if (!popup) return;

    const { delivery_uid: deliveryUid, link_url: linkUrl } = popup;
    try {
      await track(deliveryUid, "clicked");
    } catch (error) {
      console.error("[SitePopup] Failed to track click", error);
    } finally {
      window.location.assign(linkUrl);
    }
  };

  if (!popup) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label={popup.banner_alt}>
      <div className="relative w-full max-w-2xl">
        <button
          type="button"
          onClick={dismiss}
          aria-label="閉じる"
          className="absolute left-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-5 w-5" />
        </button>
        <button type="button" onClick={openLink} className="block w-full overflow-hidden rounded-theme-lg border-2 border-black bg-white shadow-theme">
          <img
            src={popup.banner_image_url}
            alt={popup.banner_alt}
            className="block h-auto max-h-[80vh] w-full object-contain"
          />
        </button>
      </div>
    </div>
  );
};

export default SitePopupHost;
